import React, { useState, useEffect } from 'react';
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Box,
    Typography,
    Chip
} from '@mui/material';
import {
    Business as CompanyIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import API from '../../../../../API';

/**
 * Company Selector Component
 *
 * Autocomplete dropdown for selecting companies.
 * Used in bank account management to choose which company to manage.
 */
function CompanySelector({ value, onChange }) {
    const [inputValue, setInputValue] = useState('');
    const [isReady, setIsReady] = useState(false);

    // Reset ready state on mount
    useEffect(() => {
        setIsReady(false);
    }, []);

    // Fetch companies list - fetch all to bypass pagination
    const { data: companies = [], isLoading, isFetching, error } = useQuery(
        ['companies'],
        async () => {
            const response = await API.companies.list({ limit: 10000 });
            return response?.companies || [];
        },
        {
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnMount: 'always',
            refetchOnReconnect: false,
            retry: 1
        }
    );

    // Track readiness - wait for data to be fully loaded before rendering Autocomplete
    useEffect(() => {
        if (!isLoading && !isFetching && companies && companies.length >= 0) {
            const timer = setTimeout(() => setIsReady(true), 50);
            return () => clearTimeout(timer);
        }
    }, [isLoading, isFetching, companies]);

    const handleChange = (event, newValue) => {
        onChange(newValue);
    };

    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
    };

    // Ensure options is always an array
    const companiesOptions = Array.isArray(companies) ? companies : [];

    // Show error state if fetch failed
    if (error) {
        return (
            <TextField
                label="Select Company"
                variant="outlined"
                error
                helperText="Failed to load companies"
                fullWidth
                disabled
            />
        );
    }

    if (!isReady) {
        return (
            <TextField
                label="Select Company"
                variant="outlined"
                placeholder="Loading companies..."
                fullWidth
                disabled
                InputProps={{
                    endAdornment: <CircularProgress color="inherit" size={20} />,
                }}
            />
        );
    }

    return (
        <Autocomplete
            key={`autocomplete-${companies.length}`}
            value={value || null}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            options={companiesOptions}
            getOptionLabel={(option) => {
                if (typeof option === 'string') {
                    return option;
                }
                return `${option.name || 'Unnamed Company'} ${option.id ? `(ID: ${option.id})` : ''}`;
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoading || isFetching}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Select Company"
                    placeholder="Search for a company..."
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <CompanyIcon sx={{ mr: 1, color: 'action.active' }} />
                                {params.InputProps.startAdornment}
                            </>
                        ),
                        endAdornment: (
                            <>
                                {(isLoading || isFetching) ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <CompanyIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1">
                                {option.name || 'Unnamed Company'}
                            </Typography>
                            {option.id && (
                                <Typography variant="caption" color="text.secondary">
                                    ID: {option.id}
                                </Typography>
                            )}
                        </Box>
                        {option.is_active === false && (
                            <Chip label="Inactive" size="small" color="default" />
                        )}
                    </Box>
                </Box>
            )}
            noOptionsText={
                (isLoading || isFetching)
                    ? 'Loading companies...'
                    : error
                        ? 'Error loading companies'
                        : 'No companies found'
            }
            fullWidth
        />
    );
}

export default CompanySelector;
